import useHttpRequest from "@hooks/useHttpRequest";
import { DeleteAsync } from "src/common/httpRequests";
import { CATALOG_SERVICE_URL } from "src/constants/api";
import { API_GATEWAY } from "src/constants/settings";
import { COMPLIANCE_STANDARD_MAP } from "../constants";
import { ITax } from "../types";

const ENDPOINT = "Taxes";

export interface ITaxesResponse {
  taxesList: ITax[];
  totalRecords: number;
  totalFilteredRecords: number;
}

export interface ITaxResponse {
  tax: ITax;
}

export interface ITaxPayload {
  taxDto: ITax;
}

const mapTaxData = (item: any): ITax => {
  return {
    ...item,
    id: item.taxId,
    complianceStandard:
      typeof item.complianceStandard === "string"
        ? COMPLIANCE_STANDARD_MAP[item.complianceStandard] ??
          item.complianceStandard
        : item.complianceStandard,
  };
};

export const useTaxApi = () => {
  const { GetAsync, PostAsync, PutAsync } = useHttpRequest();

  const getTaxes = async (
    page: number,
    record: number,
    complianceStandard: string | null,
    code?: string,
    taxType?: string,
  ) => {
    try {
      const response = await GetAsync(
        `${API_GATEWAY}/${CATALOG_SERVICE_URL}/${ENDPOINT}/List`,
        undefined,
        undefined,
        {
          params: {
            complianceStandard,
            code,
            taxType,
            page,
            record,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error(error, "error fetching taxes");
      throw error;
    }
  };
  const getTaxById = async (id: string) => {
    try {
      const response = await GetAsync(
        `${API_GATEWAY}/${CATALOG_SERVICE_URL}/${ENDPOINT}/${id}`,
      );
      const data = response.data;
      return data ? mapTaxData(data) : data;
    } catch (error) {
      console.error(error, "error fetching tax by id");
      throw error;
    }
  };

  const createTax = async (payload: ITax) => {
    try {
      let { id, complianceStandard, ...rest } = payload;

      complianceStandard =
        complianceStandard === "General" ? null : complianceStandard;

      const response = await PostAsync(
        `${API_GATEWAY}/${CATALOG_SERVICE_URL}/${ENDPOINT}`,
        {
          taxDto: { ...rest, taxId: id, complianceStandard },
        },
      );
      return response;
    } catch (error) {
      console.error(error, "error creating tax");
      throw error;
    }
  };

  const updateTax = async (payload: ITax) => {
    try {
      let { id, complianceStandard, ...rest } = payload;

      complianceStandard =
        complianceStandard === "General" ? null : complianceStandard;

      const response = await PutAsync(
        `${API_GATEWAY}/${CATALOG_SERVICE_URL}/${ENDPOINT}`,
        { taxDto: { ...rest, id: id, complianceStandard } },
      );
      return response;
    } catch (error) {
      console.error(error, "error updating tax");
      throw error;
    }
  };

  const deleteTax = async (id: string) => {
    try {
      const response = await DeleteAsync(
        `${API_GATEWAY}/${CATALOG_SERVICE_URL}/${ENDPOINT}/${id}`,
      );
      return response;
    } catch (error) {
      console.error(error, "error deleting tax");
      throw error;
    }
  };

  return {
    getTaxes,
    getTaxById,
    createTax,
    updateTax,
    deleteTax,
  };
};
